'use client'

import { motion } from 'framer-motion'
import { Home, Ear, Target, FlaskConical, TrendingUp } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

interface JourneyCardProps {
  Icon: LucideIcon
  title: string
  description: string
}

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 },
  },
}

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 },
  },
}

const JourneyCard: React.FC<JourneyCardProps> = ({
  Icon,
  title,
  description,
}) => (
  <motion.div variants={fadeIn} className="h-full">
    <div className="bg-white p-6 sm:p-8 rounded-xl shadow-md ring-1 ring-border transition-transform will-change-transform hover:-translate-y-2 h-full">
      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4 sm:mb-6">
        <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600" />
      </div>
      <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">
        {title}
      </h3>
      <p className="text-sm sm:text-base text-gray-700">{description}</p>
    </div>
  </motion.div>
)

const TherapySessionJourney: React.FC = () => {
  const journeyPoints = [
    {
      Icon: Home,
      title: 'A Safe & Non-Judgmental Space',
      description:
        'Our therapists create a warm, confidential, and accepting environment where you can feel safe to express yourself openly and honestly without fear of judgment.',
    },
    {
      Icon: Ear,
      title: 'Active Listening & Understanding',
      description:
        "Your therapist will listen attentively to your concerns, helping you to feel heard and understood. They'll ask thoughtful questions to explore your experiences more deeply.",
    },
    {
      Icon: Target,
      title: 'Collaborative Goal Setting',
      description:
        'Together, you and your therapist will identify your goals for therapy and develop a flexible plan to work towards them.',
    },
    {
      Icon: FlaskConical,
      title: 'Evidence-Based Techniques',
      description:
        'Our therapists utilize a range of scientifically supported therapeutic approaches (such as CBT, DBT, psychodynamic therapy, etc.) tailored to your needs to help you gain insights and skills.',
    },
    {
      Icon: TrendingUp,
      title: 'Focus on Growth & Skill‑Building',
      description:
        'Therapy often involves learning new coping strategies, communication skills, problem‑solving techniques, and ways to manage emotions effectively. Your therapist will help you develop practical tools that you can apply in your daily life.',
    },
  ]

  return (
    <section className="py-12 sm:py-16 lg:py-20 px-4 bg-emerald-50">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-10 sm:mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          variants={fadeIn}
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-gray-900 mb-4">
            Your Therapeutic Journey: What to Expect in Sessions
          </h2>
          <p className="text-base sm:text-lg text-gray-700 max-w-3xl mx-auto">
            Therapy is a unique experience for everyone, but here's a general
            idea of what you can expect:
          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          variants={staggerContainer}
        >
          {journeyPoints.map(({ Icon, title, description }, idx) => (
            // Last card (5th) spans full width on sm so it's centred in the 2-col grid
            <div
              key={idx}
              className={
                idx === journeyPoints.length - 1 &&
                journeyPoints.length % 2 !== 0
                  ? 'sm:col-span-2 sm:max-w-md sm:mx-auto w-full'
                  : ''
              }
            >
              <JourneyCard
                Icon={Icon}
                title={title}
                description={description}
              />
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

export default TherapySessionJourney
